import React, { useEffect, useState } from 'react';
import { Layout } from '@/components/Layout/Layout';
import { MainHeading } from '@/components/common/MainHeading';
import { SectionCard } from '@/components/common/SectionCard';
import FormField from '@/components/common/FormField';
import Button from '@/components/common/Button';
import { useForm, Controller } from 'react-hook-form';
import { callApi, ThrowError } from '@/api/callApi';
import { API_ENDPOINT, API_URL } from '@/api/constant';
import { IRoleResponse } from '@/interfaces/Auth/auth.interface';
import { showToastNotification } from '@/utility/helper';
import { Send, CheckCircle2, ChevronLeft } from 'lucide-react';
import ConfirmationModal from '@/components/common/ConfirmationModal';
import { useAppSelector } from '@/redux/store';
import { useRoleNavigate } from '@/hooks/useRoleNavigate';
import { RoutesLink } from '@/utility/RouteLinks';
import { useActiveRole } from '@/hooks/useActiveRole';
import { useNavigate } from 'react-router-dom';
import { errorTypes } from '@/utility/validation';

interface FormValues {
    roleId: string;
    message: string;
}

interface IRole {
    id: 'store' | 'driver' | 'customer' | 'staff' | 'all';
    name: string;
}

export default function CreateNotification() {
    const navigate = useNavigate();
    const { prefix } = useActiveRole();
    const { user } = useAppSelector((state) => state.user);

    const [roles, setRoles] = useState<IRole[]>([
        {
            id: 'all',
            name: 'All Users'
        },
        {
            id: 'store',
            name: 'Store Manager'
        }, {
            id: 'driver',
            name: 'Driver'
        }, {
            id: 'customer',
            name: 'Customer'
        }, {
            id: 'staff',
            name: 'Staff'
        }]);
    const [loadingRoles, setLoadingRoles] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formData, setFormData] = useState<FormValues | null>(null);

    const {
        register,
        handleSubmit,
        reset,
        control,
        formState: { errors },
    } = useForm<FormValues>({
        defaultValues: {
            roleId: '',
            message: '',
        }
    });

    // const fetchRoles = async () => {
    //     setLoadingRoles(true);
    //     try {
    //         const response = await callApi<IRoleResponse>(API_URL + API_ENDPOINT.roles, {
    //             method: "GET"
    //         });
    //         setRoles(response?.data || []);
    //     } catch (error) {
    //         ThrowError(error);
    //     } finally {
    //         setLoadingRoles(false);
    //     }
    // };

    // useEffect(() => {
    //     fetchRoles();
    // }, []);

    const handlePreSubmit = (data: FormValues) => {
        setFormData(data);
        setShowConfirm(true);
    };

    const handleConfirmSend = async () => {
        if (!formData || !user) return;

        setIsSubmitting(true);
        try {
            await callApi(`${API_URL}${API_ENDPOINT.createNotification}`, {
                method: 'POST',
                data: {
                    receiverType: formData.roleId,
                    message: formData.message,
                    payload: {
                        type: "global",
                    }
                }
            });

            showToastNotification('success', 'Notification sent successfully.');
            setShowConfirm(false);
            navigate(prefix + RoutesLink.Notifications);
        } catch (error) {
            ThrowError(error);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Layout>
            <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                <MainHeading
                    title="Send Global Notification"
                    description="Create and broadcast a notification to all users assigned to a specific role."
                    showBack
                    onBack={() => navigate(-1)}
                />

                <SectionCard title="Notification Details" icon={<Send className="w-5 h-5" />}>
                    <form onSubmit={handleSubmit(handlePreSubmit)} className="space-y-6">
                        <div className="grid grid-cols-1 gap-6">
                            <Controller
                                name="roleId"
                                control={control}
                                rules={{ required: 'Please select a role' }}
                                render={({ field }) => (
                                    <FormField
                                        label="Target User Role"
                                        type="select"
                                        options={roles.map(r => ({ label: r.name, value: r.id }))}
                                        error={errors.roleId?.message}
                                        requiredIcon
                                        value={field.value}
                                        onValueChange={field.onChange}
                                    />
                                )}
                            />

                            <FormField
                                label="Message Content"
                                type="textarea"
                                placeholder="Write the notification message that users will receive..."
                                className="min-h-[150px] text-base"
                                error={errors.message?.message}
                                requiredIcon
                                {...register('message', {
                                    ...errorTypes.required('Message is required'),
                                    minLength: { value: 5, message: 'Message must be at least 5 characters' }
                                })}
                            />
                        </div>

                        <div className="flex justify-end gap-3 pt-6 border-t border-border">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => navigate(-1)}
                                disabled={isSubmitting}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                leftIcon={<CheckCircle2 className="w-4 h-4" />}
                                isLoading={isSubmitting}
                            >
                                Broadcast Notification
                            </Button>
                        </div>
                    </form>
                </SectionCard>
            </div>

            <ConfirmationModal
                open={showConfirm}
                onOpenChange={setShowConfirm}
                onConfirm={handleConfirmSend}
                title="Send Global Broadcast?"
                message="This message will be sent to ALL users in the selected role. This action is permanent and cannot be recalled."
                confirmText="Yes, Send Now"
                cancelText="No, Edit Message"
                loading={isSubmitting}
                type="warning"
                size="lg"
            />
        </Layout>
    );
}
